import React from 'react';
import { adalApiFetch } from './adalConfig';
import Paper from '@material-ui/core/Paper';
import Box from '@mui/material/Box';
import { Autocomplete, TextField, Typography, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { ViewState, EditingState, GroupingState, IntegratedGrouping, IntegratedEditing 
} from '@devexpress/dx-react-scheduler';
import { DateCalendar, LocalizationProvider, PickersDay, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import itLocale from 'date-fns/locale/it';
import {
  Scheduler,
  Resources,
  DayView,
  WeekView,
  MonthView,
  Toolbar,
  ViewSwitcher,
  GroupingPanel,
  DateNavigator,
  TodayButton,
  Appointments,
  AppointmentTooltip,
  AppointmentForm,
} from '@devexpress/dx-react-scheduler-material-ui';
import 'bootstrap/dist/css/bootstrap.min.css';
import { endOfMonth, endOfWeek, format, isSameDay, isBefore, isValid, startOfMonth, startOfWeek } from 'date-fns';
import { blue, orange, red } from '@mui/material/colors';
import { CSVLink } from 'react-csv';

const csv_headers = [
     { label: 'Cognome', key: 'cognome' },
     { label: 'Nome', key: 'nome' },
     { label: 'Data di Nascita', key: 'dataNascita' },
     { label: 'Codice Fiscale', key: 'codiceFiscale' },
     { label: 'Telefono', key: 'telefono' },
           { label: 'Email', key: 'email' },
     { label: 'Data Visita', key: 'dataVisita' },
     { label: 'Ora Visita', key: 'oraVisita' },
   ];
   
const resources = [{
     fieldName: 'roomId',
     title: 'Ambulatorio',
     instances: [
       { text: 'Medico 1', id: 1, color: blue },
       { text: 'Medico 2', id: 2, color: orange },
     ],
   }];

const grouping = [{
     resourceName: 'roomId',
}];

const Content = ({appointmentData, ...restProps}) => (
     <AppointmentTooltip.Content {...restProps} appointmentData={appointmentData}>
          <Grid container alignItems="center">
               <Grid item xs={2}>
                    {' '}
               </Grid>
               <Grid item xs={10}>
                    <span>Codice PRN: {appointmentData.codice}</span>
               </Grid>          
          </Grid>          
          <Grid container alignItems="center">
               <Grid item xs={2}>
                    {' '}
               </Grid>
               <Grid item xs={10}>
                    <span>{appointmentData.note}</span>
               </Grid>          
          </Grid>          
     </AppointmentTooltip.Content>
);

const messages = {
     commitCommand: 'Salva',     
};

const AppointmentDatesContext = React.createContext({
     dates: new Set(),
     onMonthChange: () => {},
});

const AppointmentDay = (props) => {
     const { dates } = React.useContext(AppointmentDatesContext);
     const hasAppointment = props.day && dates.has(format(props.day, 'yyyy-MM-dd'));
     return <PickersDay
          {...props}
          sx={hasAppointment ? {
               outline: '2px solid #fbc02d',
               outlineOffset: '-2px',
          } : undefined}
     />;
};

const AppointmentCalendar = ({ selectedDate, onSelectedDateChange }) => {
     const { onMonthChange } = React.useContext(AppointmentDatesContext);
     return <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={itLocale}>
          <DateCalendar
               value={selectedDate}
               onChange={onSelectedDateChange}
               onMonthChange={onMonthChange}
               slots={{ day: AppointmentDay }}
          />
     </LocalizationProvider>;
};

async function getServerErrorMessage(result) {
     try {
          const text = await result.text();
          if (!text) {
               return `Errore ${result.status}`;
          }

          try {
               const parsed = JSON.parse(text);
               if (typeof parsed === 'string') {
                    return parsed;
               }
               if (parsed && typeof parsed.message === 'string') {
                    return parsed.message;
               }
               if (parsed && typeof parsed.error === 'string') {
                    return parsed.error;
               }
          } catch (e) {
               // Se il corpo non è JSON, usa il testo grezzo.
          }

          return text;
     } catch (error) {
          return `Errore ${result.status}`;
     }
}

function extractCsvData(listaVisite, date){
     const csvData = [];
     var i;
          
     for(i=0;i<listaVisite.length; i++){
          if(isSameDay(date, new Date(listaVisite[i].data_visita))){
               const row = {};
               row.dataVisita = format(date, "dd/MM/yyyy");
               row.oraVisita = listaVisite[i].ora_visita.slice(11,16);
               row.nome = listaVisite[i].nome;
               row.cognome = listaVisite[i].cognome;
               row.telefono = listaVisite[i].telefono;
               row.email = listaVisite[i].email;
               row.dataNascita = format(new Date(listaVisite[i].data_nascita), "dd/MM/yyyy");
               row.codiceFiscale = listaVisite[i].codice_fiscale;
               csvData.push(row);
          }
     }

     return csvData
}

function capitalize(mySentence){
     const words = mySentence.split(" ");
     
     for (let i = 0; i < words.length; i++) {
         words[i] = words[i][0] ? words[i][0].toUpperCase() + words[i].substr(1).toLowerCase() : words[i];
     }
   
     return words.join(" ");
}

const BasicLayout = ({ onFieldChange, appointmentData, availableDates, ...restProps }) => {
     const [rooms, setRooms] = React.useState([{id: 1, label: 'Medico 1'}, {id: 2, label: 'Medico 2'}]);
     const [assoToVisit, setAssoToVisit] = React.useState([]);
     const [associated, setAssociated] = React.useState(null);
     const [room, setRoom] = React.useState(appointmentData.roomId ? rooms.find(r => r.id == appointmentData.roomId) : null);

     const onAssociatedChange = (event, nextValue) => {
          onFieldChange({ associated: nextValue });
          onFieldChange({ title: nextValue.label });
          setAssociated(nextValue);
        };
     const onStartDateChange = (nextValue) => {
          onFieldChange({ startDate: nextValue });
        };
     const onEndDateChange = (nextValue) => {
          onFieldChange({ endDate: nextValue });
        };
     const onNoteChange = (event) => {
          onFieldChange({ note: event.target.value });
        };
     const onRoomChange = (event, nextValue) => {
          onFieldChange({ roomId: nextValue.id });
          setRoom(nextValue);
        };
     
     const selectedDateKey = appointmentData.startDate ? format(appointmentData.startDate, 'yyyy-MM-dd') : null;
     const isAvailable = availableDates === null || (selectedDateKey && availableDates.has(selectedDateKey));

     React.useEffect(() => {
          const fetchAssociati = async () => {
               try {
               const response = await adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI + '/visits/associatidavisitare');
               const listaAss = await response.json();
          
               let result = [];
          
               if (appointmentData.associated) {
                    result.push(appointmentData.associated);
               }
          
               // associati da visitare
               for (let i = 0; i < listaAss.length; i++) {
                    let a = {
                         id: listaAss[i].id_associato,
                         label: `${capitalize(listaAss[i].nome)} ${capitalize(listaAss[i].cognome)} (${listaAss[i].telefono})`,
                         data_scadenza: new Date(listaAss[i].data_scadenza),
                         voucher: listaAss[i].voucher,
                         ultima_visita: listaAss[i].ultima_visita ? new Date(listaAss[i].ultima_visita) : null,
                    };

                    // Aggiungi solo se non già presente
                    if (!result.some(item => item.id === a.id)) {
                         result.push(a);
                    }               
               }
          
               setAssoToVisit(result);
               } catch (error) {
               console.error('Errore nel fetch degli associati:', error);
               }
          };
          
          fetchAssociati();
     }, []);
        
     React.useEffect(() => {
          if(!associated){
               setAssociated((appointmentData.associated && assoToVisit.length > 0) ?
                    assoToVisit.find(a => a.id === appointmentData.associated.id) : null);
          }
     });

     const today = new Date();

     return <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={itLocale}>
          <Grid container spacing={2} direction="row" justifyContent="space-between">
               <Grid item xs={12} md={12}>
               <Typography variant="h5" gutterBottom component="div">
                    {format(new Date(appointmentData.startDate), 'iiii, dd MMMM yyyy', {locale: itLocale})}
               </Typography>
               </Grid>               
               <Grid item xs={6} md={6}>
               <TimePicker
                    label="Ora inizio"
                    value={appointmentData.startDate}
                    onChange={onStartDateChange}
                    slotProps={{ textField: { variant: 'outlined' } }}
               />
               </Grid>               
               <Grid item xs={12} md={12}>
               <Autocomplete
                    disablePortal
                    disableClearable
                    value={associated}
                    fullWidth
                    id="associato"
                    options={assoToVisit}
                    onChange={onAssociatedChange}
                    isOptionEqualToValue={(option, value) => option.id === value.id}                    
                    renderOption={(props, option) => {
                         const oggi = new Date();
                         const quattroMesiFa = new Date();
                         quattroMesiFa.setMonth(oggi.getMonth() - 4);

                         const scaduto = isBefore(option.data_scadenza, oggi);
                         const visitaRecente = option.ultima_visita && option.ultima_visita > quattroMesiFa;
                         const isRosso = scaduto || visitaRecente;

                         // Formatta le date per il tooltip
                         const dataScadenza = option.data_scadenza ? format(option.data_scadenza, 'dd/MM/yyyy') : 'N/D';
                         const ultimaVisita = option.ultima_visita && !isNaN(option.ultima_visita) ? format(option.ultima_visita, 'dd/MM/yyyy') : 'N/D';

                         const contenutoTooltip = `Scadenza tessera: ${dataScadenza}\nUltima visita: ${ultimaVisita}`;

                         const listItem = (
                              <li
                                   {...props}
                                   style={{
                                        color: isRosso ? red[500] : 'inherit'
                                   }}
                              >
                                   {option.label}
                              </li>
                         );

                         return isRosso
                              ? (
                                   <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{contenutoTooltip}</span>} arrow>
                                        {listItem}
                                   </Tooltip>
                              )
                              : listItem;
                    }}
                    renderInput={(params) => (
                         <TextField
                              {...params}
                              label="Associato"
                              autoFocus
                         />
                    )}
                    />
                    {associated && !associated.voucher && (
                    <Typography variant="subtitle1" color="error">
                         Nessun voucher valido!
                    </Typography>
                    )}
                    {selectedDateKey && !isAvailable && (
                    <Typography variant="subtitle1" color="error">
                         Data non disponibile: scegli una data presente nel calendario dei medici.
                    </Typography>
                    )}
                    {associated && isBefore(associated.data_scadenza, today) && (
                    <Typography variant="subtitle1" color="error">
                         Iscrizione scaduta il {format(new Date(associated.data_scadenza), 'dd/MM/yyyy', { locale: itLocale })}
                    </Typography>                    
                    )}
                 </Grid>               
               <Grid item xs={12} md={12}>
               <Autocomplete
                    disablePortal
                    disableClearable
                    value={room}
                    fullWidth
                    id="room"
                    options={rooms}
                    onChange={onRoomChange}
                    renderInput={(params) => <TextField {...params} label="Medico"/>}
                    />
               </Grid>               
               <Grid item xs={12} md={12}>
               <TextField
                    id="note"
                    label="Note"
                    placeholder="Note"
                    value={appointmentData.note}
                    margin="normal"
                    onChange={onNoteChange}
                    multiline
                    fullWidth
                    rows={4}
               />               
               </Grid>               
          </Grid>
          </LocalizationProvider>          
};

class Visite extends React.Component {
     constructor(props) {
          super(props);
          this.state = {
               currentDate: new Date(),
               appointments: [],
               addedAppointment: {},
               appointmentChanges: {},
               editingAppointment: undefined,
               csvData: [],
               availableDates: null,
               appointmentDates: new Set(),
               pendingUnavailableAppointment: null,
          };
          this.currentDateChange = this.currentDateChange.bind(this);
          this.fetchData = this.fetchData.bind(this);
          this.commitChanges = this.commitChanges.bind(this);
          this.addAppointment = this.addAppointment.bind(this);
          this.delAppointment = this.delAppointment.bind(this);
          this.updAppointment = this.updAppointment.bind(this);
          this.fetchAppointmentDates = this.fetchAppointmentDates.bind(this);
          this.appointmentDatesMonth = null;
          this.appointmentDatesRequestId = 0;
     }

     currentDateChange(currentDate){ 
          this.fetchData(currentDate);
          this.fetchDisponibilita(currentDate);
          this.fetchAppointmentDates(currentDate);
          this.setState({ currentDate }); 
     };          

	componentDidMount() {
          this.fetchData(this.state.currentDate);
          this.fetchDisponibilita(this.state.currentDate);
          this.fetchAppointmentDates(this.state.currentDate);
	}     
    
     dateKey(date) {
          return date && isValid(date) ? format(date, 'yyyy-MM-dd') : null;
     }

     isDateAvailable(date) {
          const { availableDates } = this.state;
          if (availableDates === null) return true;
          const key = this.dateKey(date);
          return key ? availableDates.has(key) : false;
     }

     parseDisponibilitaData(data) {
          const result = new Set();
          if (!Array.isArray(data)) return result;

          data.forEach(item => {
               if (!item) return;

               const start = item.data_inizio ? new Date(item.data_inizio) : (item.data ? new Date(item.data) : null);
               const end = item.data_fine ? new Date(item.data_fine) : start;
               if (start && isValid(start)) {
                    const ed = end && isValid(end) ? end : start;
                    const cur = new Date(start);
                    while (cur <= ed) {
                         result.add(format(cur, 'yyyy-MM-dd'));
                         cur.setDate(cur.getDate() + 1);
                    }
                    return;
               }

               if (typeof item === 'string') {
                    const dt = new Date(item);
                    if (isValid(dt)) result.add(format(dt, 'yyyy-MM-dd'));
                    return;
               }

               const dateVal = item.data || item.date || item.giorno || item.day;
               const dv = dateVal ? new Date(dateVal) : null;
               if (dv && isValid(dv)) {
                    result.add(format(dv, 'yyyy-MM-dd'));
               }
          });

          return result;
     }

     fetchDisponibilita(date) {
          const from = this.dateKey(date || new Date());
          const url = process.env.REACT_APP_BASE_SERVER_URI + '/visits/disponibilita?from=' + from + '&disponibilita=1';
          adalApiFetch(fetch, url)
            .then(result => {
               if (!result.ok) throw new Error('no-data');
               return result.json();
            })
            .then(data => {
               const availableDates = this.parseDisponibilitaData(data);
               this.setState({ availableDates });
            })
            .catch(error => {
               console.error('Errore nel caricamento delle disponibilità:', error);
               this.setState({ availableDates: null });
            });
     }

     fetchAppointmentDates(date, force = false) {
          const monthKey = format(date, 'yyyy-MM');
          if (!force && this.appointmentDatesMonth === monthKey) return;

          this.appointmentDatesMonth = monthKey;
          const requestId = ++this.appointmentDatesRequestId;
          const from = format(startOfWeek(startOfMonth(date), { weekStartsOn: 1 }), 'yyyy-MM-dd');
          const to = format(endOfWeek(endOfMonth(date), { weekStartsOn: 1 }), 'yyyy-MM-dd');
          const url = process.env.REACT_APP_BASE_SERVER_URI
               + '/visits/appuntamenti?datai=' + from + '&dataf=' + to;

          this.setState({ appointmentDates: new Set() });
          adalApiFetch(fetch, url)
               .then(response => {
                    if (!response.ok) throw new Error(`Errore ${response.status}`);
                    return response.json();
               })
               .then(visits => {
                    if (!Array.isArray(visits)) throw new Error('Risposta appuntamenti non valida');
                    const dates = new Set();
                    visits.forEach(visit => {
                         const visitDate = visit && visit.data_visita ? new Date(visit.data_visita) : null;
                         if (visitDate && isValid(visitDate)) {
                              dates.add(format(visitDate, 'yyyy-MM-dd'));
                         }
                    });
                    if (requestId === this.appointmentDatesRequestId) {
                         this.setState({ appointmentDates: dates });
                    }
               })
               .catch(error => {
                    if (requestId === this.appointmentDatesRequestId) {
                         this.appointmentDatesMonth = null;
                         this.setState({ appointmentDates: new Set() });
                         console.error('Errore nel caricamento delle date con visite:', error);
                    }
               });
     }

     fetchData(date){
          const datei = new Date(date).setDate(date.getDate());
          const datef = new Date(date).setDate(date.getDate());
          adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI+
                    '/visits/appuntamenti?datai='+format(datei,"yyyy-MM-dd")
                    +'&dataf='+format(datef,"yyyy-MM-dd"))
          .then(result=>{return result.json()})
          .then(listaVisite => {
               var result = [];
               var i, endDate;
               
               for(i=0;i<listaVisite.length; i++){
                    result[i] = {};
                    result[i].id = listaVisite[i].id_visita;
                    result[i].startDate = new Date(listaVisite[i].data_visita.slice(0,11)+listaVisite[i].ora_visita.slice(11,19));
                    endDate = new Date(result[i].startDate);
                    endDate.setMinutes(endDate.getMinutes() + 10);
                    result[i].endDate = endDate;
                    result[i].title = listaVisite[i].nome + ' ' + listaVisite[i].cognome;
                    result[i].associated = {id: listaVisite[i].id_associato, label: result[i].title};
                    result[i].telefono = listaVisite[i].telefono;
                    result[i].email = listaVisite[i].email;
                    result[i].tipo = listaVisite[i].tipo;
                    result[i].stato = listaVisite[i].stato;
                    result[i].codice = listaVisite[i].codice;
                    result[i].note = listaVisite[i].note;
                    result[i].roomId = listaVisite[i].id_ambulatorio;
               }
               
               this.setState({appointments: Object.values(result), csvData: extractCsvData(listaVisite, date)});
          });
     }

     addAppointment(a){
          adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI+'/visits/insappointment', {
			method: 'post',
			headers: {
				'Accept': 'application/json, application/xml, text/play, text/html, *.*',
				'Content-Type': 'application/json'
				},
			body: JSON.stringify({appuntamento: {
                              data: a.startDate, 
                              orario: format(a.startDate, 'H:m', {locale: itLocale}),
                              id_associato: a.associated.id,
                              tipoVisita: 1,
                              note: a.note,
                              id_ambulatorio: a.roomId,
                         }}),
		}).then(result=>{
			if(result.status === 200){ 	
                 this.fetchData(this.state.currentDate);
                 this.fetchAppointmentDates(this.state.currentDate, true);
			}else{
			  getServerErrorMessage(result).then(message => alert(message));
			}
		}); 		          
     }

     updAppointment(a){
          adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI+'/visits/updappointment', {
			method: 'post',
			headers: {
				'Accept': 'application/json, application/xml, text/play, text/html, *.*',
				'Content-Type': 'application/json'
				},
			body: JSON.stringify({appuntamento: {
                              id_appointment: a.id,
                              data: a.startDate, 
                              orario: format(a.startDate, 'H:m', {locale: itLocale}),
                              id_associato: a.associated.id,
                              note: a.note,
                              id_ambulatorio: a.roomId,
                         }}),
		}).then(result=>{
			if(result.status === 200){ 	
                 this.fetchData(this.state.currentDate);
                 this.fetchAppointmentDates(this.state.currentDate, true);
			}else{
			  getServerErrorMessage(result).then(message => alert(message));
			}
		}); 		          
     }

     delAppointment(d){
          adalApiFetch(fetch, process.env.REACT_APP_BASE_SERVER_URI+'/visits/delappointment', {
			method: 'post',
			headers: {
				'Accept': 'application/json, application/xml, text/play, text/html, *.*',
				'Content-Type': 'application/json'
				},
			body: JSON.stringify({id_appointment: d}),
		}).then(result=>{
			if(result.status === 200){ 	
                 this.fetchData(this.state.currentDate);
                 this.fetchAppointmentDates(this.state.currentDate, true);
			}else{
			  getServerErrorMessage(result).then(message => alert(message));
			}
		}); 		          
     }

     commitChanges({ added, changed, deleted }) {
          if (added) {
               if (!this.isDateAvailable(added.startDate)) {
                    this.setState({ pendingUnavailableAppointment: added });
                    return;
               }
               this.addAppointment(added);               
          }

          if (deleted !== undefined) {
               this.delAppointment(deleted);               
          }

          if (changed !== undefined) {
               const id = Object.keys(changed)[0];
               if(id){
                    const changes = Object.keys(changed[id]);
                    const {appointments} = this.state;
                    const a = appointments.find(a => a.id == id);

                    if(a){
                         if (changed[id].startDate && !this.isDateAvailable(changed[id].startDate)) {
                              alert('Impossibile aggiornare la visita: la data selezionata non è disponibile secondo il calendario dei medici.');
                              return;
                         }
                         changes.forEach(k => a[k] = changed[id][k]);
                         this.updAppointment(a);               
                    }
               }
          }
     }

     render() {
          const { appointments, currentDate, csvData, appointmentDates} = this.state;

          return <Paper>
               <Box className="text-right pr-5">
                    <CSVLink data={csvData} headers={csv_headers} filename={"visite.csv"}>
                         Scarica appuntamenti per il {format(currentDate, 'dd MMMM yyyy', {locale: itLocale})}
                    </CSVLink>
               </Box>
               <AppointmentDatesContext.Provider value={{ dates: appointmentDates, onMonthChange: this.fetchAppointmentDates }}>
               <Scheduler data={appointments} locale={'it-IT'} height={700}>
                    <ViewState
                         defaultCurrentDate={currentDate}
                         defaultCurrentViewName="day"
                         onCurrentDateChange={this.currentDateChange}                         
                    />
                    <EditingState
                         onCommitChanges={this.commitChanges}
                    />                 
                    <GroupingState
                         grouping={grouping}
                         groupOrientation={() => 'Horizontal'}
                    />
                    <DayView
                         name="day"
                         displayName="Giorno"
                         height={10}
                         startDayHour={16}
                         endDayHour={20}
                         cellDuration={10}                    
                    />
                    
                    <Appointments />
                    <Resources
                         data={resources}
                         mainResourceName="roomId"
                    />
                    <IntegratedGrouping />
                    <IntegratedEditing />   
                    <AppointmentTooltip
                         contentComponent={Content}
                         showCloseButton
                         showOpenButton
                         showDeleteButton                    
                    />
                    <AppointmentForm
                         basicLayoutComponent={(props) => <BasicLayout {...props} availableDates={this.state.availableDates} />}
                         messages={messages}
                    />
                    <GroupingPanel />
                    <Toolbar />
                    <DateNavigator calendarComponent={AppointmentCalendar} />
                    <TodayButton />
               </Scheduler>
               </AppointmentDatesContext.Provider>
               <Dialog
                    open={Boolean(this.state.pendingUnavailableAppointment)}
                    onClose={() => this.setState({ pendingUnavailableAppointment: null })}
               >
                    <DialogTitle>Data non disponibile</DialogTitle>
                    <DialogContent>
                         La data selezionata non risulta disponibile nel calendario dei medici. Vuoi inserire comunque l'appuntamento?
                    </DialogContent>
                    <DialogActions>
                         <Button onClick={() => this.setState({ pendingUnavailableAppointment: null })}>
                              Annulla
                         </Button>
                         <Button
                              variant="contained"
                              onClick={() => {
                                   const appointment = this.state.pendingUnavailableAppointment;
                                   this.setState({ pendingUnavailableAppointment: null });
                                   if (appointment) this.addAppointment(appointment);
                              }}
                         >
                              Conferma comunque
                         </Button>
                    </DialogActions>
               </Dialog>
               </Paper>
     }
}
export default Visite;